/* ==========================================================================
   PDP fast checkout
   Powers the sticky buy bar, the quick pop-up checkout and the dedicated
   express checkout page. Loaded on the product details page (and on the
   express checkout page) only.
   ========================================================================== */

(function () {
  'use strict';

  var config = window.PDPFastCheckout || {};

  /* ---------------------------------------------------------------- utils */

  function rootUrl() {
    var root = (window.routes && window.routes.root_url) || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  // Cart permalink: builds a fresh cart with just these items and sends the
  // shopper straight to the payment step, skipping the cart page entirely.
  function directCheckoutUrl(variantId, quantity) {
    return rootUrl() + 'cart/' + variantId + ':' + (quantity || 1);
  }

  function formatMoney(cents, format) {
    var value = Number(cents);
    if (isNaN(value)) return '';

    var moneyFormat = format || config.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function thousands(number, separator) {
      var parts = number.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + separator);
      return parts;
    }

    function withDecimals(precision, thousandSep, decimalSep) {
      var amount = (value / 100.0).toFixed(precision);
      var parts = thousands(amount, thousandSep);
      return parts[1] ? parts[0] + decimalSep + parts[1] : parts[0];
    }

    // Indian stores commonly use a 2,2,3 grouping - honour it when the
    // storefront currency is INR.
    function indianGrouping(precision, decimalSep) {
      var amount = (value / 100.0).toFixed(precision);
      var parts = amount.split('.');
      var integer = parts[0];
      var lastThree = integer.slice(-3);
      var rest = integer.slice(0, -3);
      if (rest) lastThree = ',' + lastThree;
      var grouped = rest.replace(/(\d)(?=(\d\d)+$)/g, '$1,') + lastThree;
      return parts[1] ? grouped + decimalSep + parts[1] : grouped;
    }

    var match = moneyFormat.match(placeholder);
    var token = match ? match[1] : 'amount';
    var formatted;

    switch (token) {
      case 'amount':
        formatted = config.currencyCode === 'INR' ? indianGrouping(2, '.') : withDecimals(2, ',', '.');
        break;
      case 'amount_no_decimals':
        formatted = config.currencyCode === 'INR' ? indianGrouping(0, '.') : withDecimals(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDecimals(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDecimals(0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withDecimals(2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDecimals(0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDecimals(2, "'", '.');
        break;
      default:
        formatted = withDecimals(2, ',', '.');
    }

    return moneyFormat.replace(placeholder, formatted);
  }

  function productForm() {
    return document.querySelector('product-form form[data-type="add-to-cart-form"]');
  }

  function currentVariantId() {
    var form = productForm();
    var input = form && form.querySelector('.product-variant-id');
    if (input && input.value) return input.value;
    return config.variantId;
  }

  function currentQuantity() {
    var form = productForm();
    var input = form && form.querySelector('[name="quantity"]');
    var value = input ? parseInt(input.value, 10) : 1;
    return value > 0 ? value : 1;
  }

  function mainSubmitButton() {
    var form = productForm();
    return form ? form.querySelector('[type="submit"]') : null;
  }

  /* ------------------------------------------------------------ skip cart */

  var SKIP_CART_KEY = 'pdp-skip-cart';

  function skipCartEnabled() {
    try {
      var stored = window.localStorage.getItem(SKIP_CART_KEY);
      if (stored !== null) return stored === '1';
    } catch (e) {
      /* storage unavailable - fall through to the theme default */
    }
    return config.skipCartDefault === true;
  }

  function setSkipCart(enabled) {
    try {
      window.localStorage.setItem(SKIP_CART_KEY, enabled ? '1' : '0');
    } catch (e) {
      /* nothing to do - the toggles below still stay in sync in-session */
    }
    document.querySelectorAll('.pdp-skip-cart__input').forEach(function (input) {
      if (input.checked !== enabled) input.checked = enabled;
    });
  }

  // Where "skip cart" sends the shopper once the item is in the cart.
  function skipCartDestination() {
    if (config.skipCartDestination === 'checkout' || !config.expressCheckoutUrl) {
      return rootUrl() + 'checkout';
    }
    return config.expressCheckoutUrl;
  }

  function initSkipCartToggles() {
    var enabled = skipCartEnabled();
    document.querySelectorAll('.pdp-skip-cart__input').forEach(function (input) {
      input.checked = enabled;
      input.addEventListener('change', function () {
        setSkipCart(input.checked);
      });
    });
  }

  /* ---------------------------------------------------------- add to cart */

  // Adds the current selection using the real product form so variant
  // pickers, quantity rules and line item properties are all respected.
  function addToCart() {
    var form = productForm();
    if (!form) return Promise.reject(new Error('Product form not found'));

    var formData = new FormData(form);
    return fetch((window.routes && window.routes.cart_add_url) || rootUrl() + 'cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message);
        return data;
      });
  }

  /* -------------------------------------------------------- sticky buy bar */

  if (!customElements.get('pdp-sticky-bar')) {
    customElements.define(
      'pdp-sticky-bar',
      class PdpStickyBar extends HTMLElement {
        connectedCallback() {
          this.buyNowButton = this.querySelector('.pdp-sticky-bar__buy-now');
          this.addButton = this.querySelector('.pdp-sticky-bar__add');
          this.quickButton = this.querySelector('.pdp-sticky-bar__quick-link');
          this.priceTarget = this.querySelector('.pdp-sticky-bar__price');

          this.watchInlineButtons();
          this.syncFromMainForm();

          if (this.buyNowButton) {
            this.buyNowButton.addEventListener('click', this.onBuyNow.bind(this));
          }
          if (this.addButton) {
            this.addButton.addEventListener('click', this.onAddToCart.bind(this));
          }
          if (this.quickButton) {
            this.quickButton.addEventListener('click', function () {
              var modal = document.querySelector('pdp-quick-checkout');
              if (modal) modal.open();
            });
          }

          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
          }

          // The disabled state of the in-page Add to cart button can be driven
          // by other blocks (e.g. the return policy consent checkbox) - mirror
          // whatever it currently is.
          var main = mainSubmitButton();
          if (main && window.MutationObserver) {
            this.stateObserver = new MutationObserver(this.syncDisabledState.bind(this));
            this.stateObserver.observe(main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
          }
          this.syncDisabledState();
        }

        disconnectedCallback() {
          if (this.unsubscribeVariant) this.unsubscribeVariant();
          if (this.stateObserver) this.stateObserver.disconnect();
          if (this.visibilityObserver) this.visibilityObserver.disconnect();
          document.body.style.paddingBottom = '';
        }

        // Show the bar once the in-page buy buttons have scrolled out of view.
        watchInlineButtons() {
          var anchor = document.querySelector('.product-form__buttons--pdp');
          var self = this;

          if (!anchor || !window.IntersectionObserver) {
            this.show();
            return;
          }

          this.visibilityObserver = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  self.hide();
                } else {
                  self.show();
                }
              });
            },
            { rootMargin: '0px 0px -40px 0px' }
          );
          this.visibilityObserver.observe(anchor);
        }

        show() {
          this.classList.add('is-visible');
          document.body.style.paddingBottom = this.offsetHeight + 'px';
        }

        hide() {
          this.classList.remove('is-visible');
          document.body.style.paddingBottom = '';
        }

        onVariantChange(event) {
          var variant = event && event.data && event.data.variant;
          if (!variant) return;

          if (this.priceTarget) {
            this.priceTarget.textContent = formatMoney(variant.price);
          }
          this.toggleAvailability(variant.available);
        }

        syncFromMainForm() {
          if (!this.priceTarget || this.priceTarget.textContent.trim()) return;
          this.priceTarget.textContent = formatMoney(config.price);
        }

        toggleAvailability(available) {
          [this.buyNowButton, this.addButton].forEach(function (button) {
            if (!button) return;
            button.disabled = !available;
          });
        }

        syncDisabledState() {
          var main = mainSubmitButton();
          if (!main || !this.addButton) return;
          this.addButton.disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
        }

        setLoading(button, loading) {
          if (!button) return;
          button.classList.toggle('loading', loading);
          button.disabled = loading;
        }

        onBuyNow() {
          // Prefer the store's existing one-click checkout button when present
          // so the configured checkout provider stays in charge.
          var provider = document.querySelector('#gokwik-buy-now');
          if (provider && provider.offsetParent !== null) {
            provider.click();
            return;
          }

          var native = document.querySelector('.product-form__dynamic-checkout shopify-buy-it-now-button button');
          if (native && native.offsetParent !== null) {
            native.click();
            return;
          }

          window.location.href = directCheckoutUrl(currentVariantId(), currentQuantity());
        }

        onAddToCart() {
          var self = this;
          var skip = skipCartEnabled();

          if (!skip) {
            // Let the theme's own product form handle it so the cart
            // notification / drawer behaves exactly as before.
            var main = mainSubmitButton();
            if (main && !main.disabled) {
              main.click();
              return;
            }
          }

          this.setLoading(this.addButton, true);
          addToCart()
            .then(function () {
              window.location.href = skipCartDestination();
            })
            .catch(function (error) {
              console.error(error);
              self.setLoading(self.addButton, false);
              window.location.href = (window.routes && window.routes.cart_url) || rootUrl() + 'cart';
            });
        }
      }
    );
  }

  /* ----------------------------------------------------- quick pop-up checkout */

  if (!customElements.get('pdp-quick-checkout')) {
    customElements.define(
      'pdp-quick-checkout',
      class PdpQuickCheckout extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('.pdp-quick-checkout__dialog');
          this.qtyInput = this.querySelector('.pdp-quick-checkout__qty input');
          this.unitPriceTarget = this.querySelector('[data-quick-unit-price]');
          this.subtotalTarget = this.querySelector('[data-quick-subtotal]');
          this.variantTarget = this.querySelector('.pdp-quick-checkout__line-variant');
          this.imageTarget = this.querySelector('.pdp-quick-checkout__image');
          this.errorTarget = this.querySelector('.pdp-quick-checkout__error');
          this.pageLink = this.querySelector('.pdp-quick-checkout__page-link');
          this.checkoutButton = this.querySelector('.pdp-quick-checkout__submit');
          this.addButton = this.querySelector('.pdp-quick-checkout__add');

          this.unitPrice = Number(config.price) || 0;

          var self = this;

          this.querySelectorAll('[data-quick-close]').forEach(function (element) {
            element.addEventListener('click', self.close.bind(self));
          });

          this.querySelectorAll('[data-quick-step]').forEach(function (button) {
            button.addEventListener('click', function () {
              var step = parseInt(button.dataset.quickStep, 10);
              self.setQuantity(self.quantity() + step);
            });
          });

          if (this.qtyInput) {
            this.qtyInput.addEventListener('change', function () {
              self.setQuantity(self.quantity());
            });
          }

          if (this.checkoutButton) {
            this.checkoutButton.addEventListener('click', this.checkout.bind(this));
          }

          if (this.addButton) {
            this.addButton.addEventListener('click', this.addAndClose.bind(this));
          }

          this.onKeydown = function (event) {
            if (event.key === 'Escape') self.close();
          };

          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
              var variant = event && event.data && event.data.variant;
              if (!variant) return;
              self.unitPrice = variant.price;
              if (self.variantTarget) {
                self.variantTarget.textContent = variant.title || '';
                self.variantTarget.hidden = !variant.title || variant.title === 'Default Title';
              }
              if (self.imageTarget && variant.featured_image && variant.featured_image.src) {
                self.imageTarget.src = variant.featured_image.src;
              }
              self.render();
            });
          }

          this.render();
        }

        disconnectedCallback() {
          if (this.unsubscribeVariant) this.unsubscribeVariant();
          document.removeEventListener('keydown', this.onKeydown);
          document.body.classList.remove('pdp-quick-checkout-open');
        }

        quantity() {
          var value = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
          return value > 0 ? value : 1;
        }

        setQuantity(value) {
          var quantity = value > 0 ? value : 1;
          if (this.qtyInput) this.qtyInput.value = quantity;
          var minus = this.querySelector('[data-quick-step="-1"]');
          if (minus) minus.disabled = quantity <= 1;
          this.render();
        }

        render() {
          if (this.unitPriceTarget) this.unitPriceTarget.textContent = formatMoney(this.unitPrice);
          if (this.subtotalTarget) this.subtotalTarget.textContent = formatMoney(this.unitPrice * this.quantity());
          if (this.pageLink) {
            this.pageLink.href =
              (config.expressCheckoutUrl || rootUrl() + 'pages/express-checkout') +
              '?v=' +
              encodeURIComponent(currentVariantId()) +
              '&q=' +
              this.quantity() +
              '&h=' +
              encodeURIComponent(config.productHandle || '');
          }
        }

        open() {
          this.previouslyFocused = document.activeElement;
          // Start from whatever quantity the shopper picked on the page.
          this.setQuantity(currentQuantity());
          this.classList.add('is-open');
          document.body.classList.add('pdp-quick-checkout-open');
          document.addEventListener('keydown', this.onKeydown);
          var focusTarget = this.querySelector('.pdp-quick-checkout__close');
          if (focusTarget) focusTarget.focus();
        }

        close() {
          this.classList.remove('is-open');
          document.body.classList.remove('pdp-quick-checkout-open');
          document.removeEventListener('keydown', this.onKeydown);
          if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus();
        }

        showError(message) {
          if (!this.errorTarget) return;
          this.errorTarget.textContent = message;
          this.errorTarget.hidden = !message;
        }

        checkout() {
          this.showError('');
          window.location.href = directCheckoutUrl(currentVariantId(), this.quantity());
        }

        addAndClose() {
          var self = this;
          var form = productForm();
          var quantityInput = form && form.querySelector('[name="quantity"]');
          var previousQuantity = quantityInput ? quantityInput.value : null;

          if (quantityInput) quantityInput.value = this.quantity();

          this.addButton.classList.add('loading');
          this.addButton.disabled = true;

          addToCart()
            .then(function () {
              window.location.href = skipCartEnabled()
                ? skipCartDestination()
                : (window.routes && window.routes.cart_url) || rootUrl() + 'cart';
            })
            .catch(function (error) {
              self.showError(error.message || 'Something went wrong. Please try again.');
              self.addButton.classList.remove('loading');
              self.addButton.disabled = false;
              if (quantityInput && previousQuantity !== null) quantityInput.value = previousQuantity;
            });
        }
      }
    );
  }

  /* --------------------------------------------- express checkout page (direct buy) */

  if (!customElements.get('express-checkout-direct')) {
    customElements.define(
      'express-checkout-direct',
      class ExpressCheckoutDirect extends HTMLElement {
        connectedCallback() {
          var params = new URLSearchParams(window.location.search);
          this.variantId = params.get('v');
          this.quantity = parseInt(params.get('q'), 10) || 1;
          this.handle = params.get('h');

          this.cartSummary = document.querySelector('[data-express-cart-summary]');
          this.loading = this.querySelector('.express-checkout__loading');
          this.content = this.querySelector('[data-express-direct-content]');

          if (!this.variantId || !this.handle) {
            // No direct-buy parameters - the server rendered cart summary stands.
            this.hidden = true;
            return;
          }

          // A direct buy replaces the cart summary with just this item.
          if (this.cartSummary) this.cartSummary.hidden = true;
          this.load();
        }

        load() {
          var self = this;
          fetch(rootUrl() + 'products/' + this.handle + '.js')
            .then(function (response) {
              return response.json();
            })
            .then(function (product) {
              var variant =
                product.variants.filter(function (item) {
                  return String(item.id) === String(self.variantId);
                })[0] || product.variants[0];
              self.render(product, variant);
            })
            .catch(function () {
              // Fall back to the cart summary rather than showing nothing.
              self.hidden = true;
              if (self.cartSummary) self.cartSummary.hidden = false;
            });
        }

        render(product, variant) {
          var self = this;
          var total = variant.price * this.quantity;

          this.querySelectorAll('[data-express-title]').forEach(function (node) {
            node.textContent = product.title;
          });

          var variantNode = this.querySelector('[data-express-variant]');
          if (variantNode) {
            variantNode.textContent = variant.title || '';
            variantNode.hidden = !variant.title || variant.title === 'Default Title';
          }

          var qtyNode = this.querySelector('[data-express-qty]');
          if (qtyNode) qtyNode.textContent = (config.quantityLabel || 'Qty') + ': ' + this.quantity;

          var imageNode = this.querySelector('[data-express-image]');
          var imageSrc = (variant.featured_image && variant.featured_image.src) || product.featured_image;
          if (imageNode && imageSrc) {
            imageNode.src = imageSrc;
            imageNode.alt = product.title;
          } else if (imageNode) {
            imageNode.hidden = true;
          }

          this.querySelectorAll('[data-express-line-price]').forEach(function (node) {
            node.textContent = formatMoney(total);
          });
          this.querySelectorAll('[data-express-subtotal]').forEach(function (node) {
            node.textContent = formatMoney(total);
          });

          var checkoutButton = this.querySelector('[data-express-checkout]');
          if (checkoutButton) {
            checkoutButton.addEventListener('click', function () {
              window.location.href = directCheckoutUrl(self.variantId, self.quantity);
            });
          }

          if (this.loading) this.loading.hidden = true;
          if (this.content) this.content.hidden = false;
        }
      }
    );
  }

  /* ---------------------------------------------------------------- boot */

  // Keeps the no-JS fallback Buy Now link pointing at the current selection.
  function initInlineBuyNow() {
    var links = document.querySelectorAll('a.pdp-buy-now');
    if (!links.length) return;

    function sync() {
      var href = directCheckoutUrl(currentVariantId(), currentQuantity());
      links.forEach(function (link) {
        link.href = href;
      });
    }

    sync();

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.variantChange, sync);
      subscribe(PUB_SUB_EVENTS.quantityUpdate, sync);
    }

    var form = productForm();
    var quantityInput = form && form.querySelector('[name="quantity"]');
    if (quantityInput) quantityInput.addEventListener('change', sync);
  }

  function init() {
    initSkipCartToggles();
    initInlineBuyNow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
